import { useState } from 'react'
import { Divider, Stack, styled, Tabs, Typography } from '@mui/material'
import {
  Invitee,
  ResGetInvitationsPayload,
} from '@weblite/ts-action/invitation'
import { Tab } from '$common/tab'
import { UserListItem } from '$common/user-list-item'
import { SettingsSection } from '$common/settings-section'
import { InvitedItem } from '../invited-item'
import { FullPageListSkeleton } from '$common/full-page-list-skeleton'
import { openUserProfile } from '$logic/router'
import { getNonSignedUpContacts } from '$logic/contacts/service'
import { getUserDisplayName } from '$logic/users'
import { t } from '$i18n'
import { useAsyncFunction } from '$func/async-function.react-hook'

type InviteCategory = 'invited' | 'accepted'
const categories: InviteCategory[] = ['invited', 'accepted']

function a11yProps(index: number) {
  return {
    id: `invite-friends-tab-${index}`,
  }
}

const StyledDivider = styled(Divider)`
  &:last-child {
    display: none;
  }
`
function EmptyState({ text }: { text: string }) {
  return (
    <Typography variant="body2" color="gray" margin="50px auto">
      {text}
    </Typography>
  )
}
function InvitedItems({
  items,
  loading,
}: {
  items: Invitee[]
  loading: boolean
}) {
  const { value: invitedItemsInfos } = useAsyncFunction(async () => {
    const nonSingedUpContacts = await getNonSignedUpContacts()
    return items.map(({ inviteePhoneNumber, inviteDate }) => {
      const contact = nonSingedUpContacts.find(
        item => item.phone === inviteePhoneNumber,
      )
      return {
        name: getUserDisplayName(contact),
        inviteDate,
        phone: inviteePhoneNumber,
      }
    })
  }, [loading])
  if (loading) return <FullPageListSkeleton />
  if (!loading && !items.length)
    return <EmptyState text={t('emptyState.noResultFound')} />
  return (
    <>
      {invitedItemsInfos?.map(({ name, phone, inviteDate }) => (
        <>
          <InvitedItem name={name} phone={phone} date={inviteDate} />
          <StyledDivider variant="inset" />
        </>
      ))}
    </>
  )
}

function AcceptedItems({
  items,
  loading,
}: {
  items: Invitee[]
  loading: boolean
}) {
  if (loading) return <FullPageListSkeleton />
  if (!loading && !items.length)
    return <EmptyState text={t('emptyState.noUserFound')} />
  return (
    <>
      {items.map(item => (
        <>
          <UserListItem
            userId={item.inviteeId}
            onClick={() => item.inviteeId && openUserProfile(item.inviteeId)}
          />
          <StyledDivider variant="inset" />
        </>
      ))}
    </>
  )
}

export function InvitationUsersInfo({
  invitations,
  loading,
}: {
  invitations: ResGetInvitationsPayload
  loading: boolean
}) {
  const [selectedCategory, setSelectedCategory] = useState<InviteCategory>(
    'invited',
  )

  return (
    <SettingsSection sx={{ overflow: 'hidden', height: '100%' }}>
      <>
        <Tabs
          value={selectedCategory}
          onChange={(_, newValue) => setSelectedCategory(newValue)}
          sx={{ backgroundColor: 'white', marginTop: '5px' }}
        >
          {categories.map((category, index) => (
            <Tab
              label={t(`invite.${category.toLowerCase()}`)}
              value={category}
              key={category}
              {...a11yProps(index)}
              width={100 / categories.length}
            />
          ))}
        </Tabs>
        <Stack sx={{ overflowY: 'auto', backgroundColor: 'white' }}>
          {selectedCategory === 'invited' ? (
            <InvitedItems items={invitations?.pending} loading={loading} />
          ) : (
            <AcceptedItems items={invitations?.accepted} loading={loading} />
          )}
        </Stack>
      </>
    </SettingsSection>
  )
}
